import { useEffect, useCallback } from 'react'
import { useRouteMatch, useParams } from 'react-router'
import copy from 'copy-to-clipboard'
import { usePRFileSections } from '@sketch-hq/sketch-web-renderer'

import { keyWithModifierAndShift, shortcuts } from '@sketch/utils'
import { useToast } from '@sketch/toasts'
import {
  isDocumentPageRouteCanvas,
  isDocumentRoute,
  isFrameGroupRoute,
  isPreviewPageCanvasRoute,
  routes,
} from '@sketch/modules-common'

import { useVersioning } from '../../versioning'
import {
  PreviewContextProps,
  usePreview,
} from '../../shares/views/PreviewView/PreviewContext'
import { useSelectedFrame } from '../components/SelectedFrameContext'

type LinkType = 'frame' | 'page' | 'section'

interface CopyLinkResult {
  url: string
  type: LinkType
}

/**
 * Sets up a global keyboard shortcut (Cmd+Shift+C on Mac, Ctrl+Shift+C on Windows)
 * that copies the current URL based on the view context:
 *
 * 1. When in frame detail view: copies the frame URL
 * 2. When in page view with a frame selected: copies the frame URL
 * 3. When viewing a page (no frame selected): copies the page URL
 */
export function useCopyLinkShortcut(): void {
  const { showToast } = useToast()
  const { path } = useRouteMatch()
  const params = useParams<Record<string, string>>()
  const versioning = useVersioning()
  const preview = usePreview()
  const selectedFrameContext = useSelectedFrame()
  const sections = usePRFileSections()

  const createUrl = useCallback((): CopyLinkResult | null => {
    // Frame view - copy frame URL
    if (isFrameGroupRoute(path)) {
      const url = createFrameUrl(versioning, params.frameUUID)
      return url ? { url, type: 'frame' } : null
    }

    // Page canvas view with a top-level entity selected - copy its URL.
    // View-mode selection stores the selected entity's id in the frame slot
    // regardless of its kind, so resolve whether it is actually a Section and
    // build the matching URL.
    const isPageCanvasRoute =
      isDocumentPageRouteCanvas(path) || isDocumentRoute(path)
    if (isPageCanvasRoute && selectedFrameContext?.selectedFrameUUID) {
      const selectedUUID = selectedFrameContext.selectedFrameUUID
      const isSectionSelected = (sections ?? []).some(
        section => section.objectId === selectedUUID
      )

      if (isSectionSelected) {
        const url = createSectionUrl(versioning, path, params, selectedUUID)
        return url ? { url, type: 'section' } : null
      }

      const url = createFrameUrl(versioning, selectedUUID)
      return url ? { url, type: 'frame' } : null
    }

    // Page canvas view - copy page URL
    if (isPageCanvasRoute) {
      const url = createPageCanvasUrl(path, params, versioning)
      return url ? { url, type: 'page' } : null
    }

    // Preview view - copy preview URL
    const url = createPreviewUrl(path, params, preview)
    return url ? { url, type: 'page' } : null
  }, [
    path,
    params,
    versioning,
    preview,
    selectedFrameContext?.selectedFrameUUID,
    sections,
  ])

  useEffect((): (() => void) => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (!keyWithModifierAndShift(shortcuts.copyLink)(event)) {
        return
      }

      event.preventDefault()

      const result: CopyLinkResult | null = createUrl()
      if (result) {
        copy(`${window.location.origin}${result.url}`)
        showToast(
          result.type === 'frame'
            ? 'Copied link to frame'
            : result.type === 'section'
              ? 'Copied link to section'
              : 'Copied link to page'
        )
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [createUrl, showToast])
}

type Versioning = ReturnType<typeof useVersioning>

function createFrameUrl(
  versioning: Versioning,
  frameUUID: string | undefined
): string | null {
  if (versioning.loading || !frameUUID) {
    return null
  }

  return routes.FRAME.create({
    shareID: versioning.share.identifier,
    frameUUID,
  })
}

function createSectionUrl(
  versioning: Versioning,
  path: string,
  params: Record<string, string>,
  sectionUUID: string
): string | null {
  if (versioning.loading) {
    return null
  }

  const pageUUID = isDocumentPageRouteCanvas(path)
    ? params.pageUUID
    : versioning.currentVersion?.document?.pages?.entries[0]?.uuid

  if (!pageUUID) {
    return null
  }

  return routes.PAGE_SECTION.create({
    shareID: versioning.share.identifier,
    pageUUID,
    sectionUUID,
  })
}

function createPageCanvasUrl(
  path: string,
  params: Record<string, string>,
  versioning: Versioning
): string | null {
  if (versioning.loading) {
    return null
  }

  const pageUUID = isDocumentPageRouteCanvas(path)
    ? params.pageUUID
    : versioning.currentVersion?.document?.pages?.entries[0]?.uuid

  if (!pageUUID) {
    return null
  }

  return routes.SHARE_PAGE_CANVAS_VIEW.create({
    shareID: versioning.share.identifier,
    pageUUID,
  })
}

function createPreviewUrl(
  path: string,
  params: Record<string, string>,
  preview: PreviewContextProps | undefined
): string | null {
  if (!preview) {
    return null
  }

  const isPreviewPageRoute = path === routes.PREVIEW_PAGE_VIEW.template()
  const isPreviewRoute =
    path === routes.PREVIEW_VIEW.template() || isPreviewPageCanvasRoute(path)

  if (!isPreviewPageRoute && !isPreviewRoute) {
    return null
  }

  const pageUUID = isPreviewPageRoute
    ? params.pageUUID
    : preview.currentPage?.uuid

  if (!pageUUID) {
    return null
  }

  return routes.PREVIEW_PAGE_VIEW.create({
    previewID: params.previewID,
    pageUUID,
  })
}
